<template>
  <section v-if="inline" class="qsm qsm--inline">
    <header class="qsm__header qsm__header--inline modal-header">
      <div class="qsm__header-copy">
        <h3>Settings</h3>
        <p class="qsm__description">Tune the rules for this experience.</p>
      </div>
      <div class="qsm__header-actions">
        <span class="qsm__step-actions-label">Save actions</span>
        <button type="button" class="qsm__action qsm__action--primary qsm__action--inline-save" @click="handleSave">
          Save
        </button>
        <span class="qsm__step-status" :class="{ 'qsm__step-status--saved': Boolean(saveSuccess) }">
          {{ saveSuccess ? 'Saved' : 'Unsaved' }}
        </span>
      </div>
    </header>

    <div class="qsm__scroll-body qsm__scroll-body--inline modal-body">
      <div v-if="isGame" class="qsm__games-placeholder">Games coming soon</div>
      <fieldset v-else class="qsm__group">
        <legend class="qsm__group-title">Rules</legend>
        <div class="qsm__field-grid">
          <label v-if="showTimeLimitField" class="qsm__field">
            <span class="qsm__field-label">Time Limit (seconds)</span>
            <input
              type="number"
              :value="draft.timeLimitSeconds"
              min="10"
              max="300"
              @input="draft.timeLimitSeconds = Number(($event.target as HTMLInputElement).value)"
            />
          </label>

          <label v-if="showQuestionsPerRoundField" class="qsm__field">
            <span class="qsm__field-label">Questions per round</span>
            <input
              type="number"
              :value="draft.questionsPerRound"
              :min="QUESTIONS_PER_ROUND_MIN"
              :max="QUESTIONS_PER_ROUND_MAX"
              @input="draft.questionsPerRound = clampQuestionsPerRound(Number(($event.target as HTMLInputElement).value))"
            />
          </label>

          <label class="qsm__field">
            <span class="qsm__field-label">Pass Threshold</span>
            <input
              type="number"
              :value="draft.passThreshold"
              :min="1"
              :max="maxPassThreshold"
              @input="draft.passThreshold = Number(($event.target as HTMLInputElement).value)"
            />
          </label>

          <label class="qsm__field">
            <span class="qsm__field-label">Max Attempts</span>
            <input
              type="number"
              :value="draft.attemptLimit"
              min="1"
              max="10"
              @input="draft.attemptLimit = Number(($event.target as HTMLInputElement).value)"
            />
          </label>

          <label class="qsm__field">
            <span class="qsm__field-label">Attempt Window</span>
            <select
              :value="draft.attemptWindow"
              @change="draft.attemptWindow = ($event.target as HTMLSelectElement).value as QuizConfig['attemptWindow']"
            >
              <option value="week">Per Week</option>
              <option value="day">Per Day</option>
              <option value="hour">Per Hour</option>
              <option value="minute">Per Minute</option>
              <option value="custom">Custom (per-round)</option>
            </select>
          </label>

          <label v-if="draft.attemptWindow === 'week'" class="qsm__field">
            <span class="qsm__field-label">Week rolls over on</span>
            <select
              :value="draft.attemptWindowRolloverWeekday"
              @change="draft.attemptWindowRolloverWeekday = normaliseRolloverWeekday(Number(($event.target as HTMLSelectElement).value))"
            >
              <option v-for="opt in ROLLOVER_WEEKDAY_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>

          <label v-if="draft.attemptWindow === 'week' || draft.attemptWindow === 'day'" class="qsm__field">
            <span class="qsm__field-label">Rollover time (campaign TZ)</span>
            <input
              type="time"
              :value="draft.attemptWindowRolloverTime"
              @change="draft.attemptWindowRolloverTime = normaliseRolloverTime(($event.target as HTMLInputElement).value)"
            />
          </label>

          <div v-if="draft.attemptWindow === 'custom'" class="qsm__field qsm__custom-hint">
            <strong>Custom per-round windows are king.</strong>
            Configure each round's start/end time and max attempts in the
            <em>Experience Editor → Cycling Schedule → Explicit rounds</em>.
            The Attempt Limit + rollover settings above are ignored when this mode is on.
          </div>
        </div>
      </fieldset>

      <fieldset v-if="showLeaderboardSection" class="qsm__group">
        <legend class="qsm__group-title">Leaderboard</legend>
        <div class="qsm__field-grid">
          <div class="qsm__field qsm__field--toggle">
            <span class="qsm__field-label">Enable leaderboard</span>
            <button
              type="button"
              class="qsm__toggle"
              :class="{ 'qsm__toggle--active': draft.leaderboard.enabled }"
              :aria-pressed="draft.leaderboard.enabled"
              @click="draft.leaderboard.enabled = !draft.leaderboard.enabled"
            >
              <span class="qsm__toggle-thumb" />
            </button>
          </div>

          <div class="qsm__field qsm__field--toggle">
            <span class="qsm__field-label">Show after completion only</span>
            <button
              type="button"
              class="qsm__toggle"
              :class="{ 'qsm__toggle--active': draft.leaderboard.showAfterCompletionOnly }"
              :aria-pressed="draft.leaderboard.showAfterCompletionOnly"
              @click="draft.leaderboard.showAfterCompletionOnly = !draft.leaderboard.showAfterCompletionOnly"
            >
              <span class="qsm__toggle-thumb" />
            </button>
          </div>

          <label class="qsm__field">
            <span class="qsm__field-label">Leaderboard title</span>
            <input
              type="text"
              :value="draft.leaderboard.title"
              maxlength="48"
              placeholder="Top Players"
              @input="draft.leaderboard.title = ($event.target as HTMLInputElement).value"
            />
          </label>

          <label class="qsm__field">
            <span class="qsm__field-label">Empty state text</span>
            <input
              type="text"
              :value="draft.leaderboard.emptyStateText"
              maxlength="120"
              placeholder="Be the first to post a score."
              @input="draft.leaderboard.emptyStateText = ($event.target as HTMLInputElement).value"
            />
          </label>
        </div>
      </fieldset>

      <!-- Colours hidden — functionality not yet wired up -->
    </div>

  </section>

  <teleport v-else to="body">
    <div
      v-if="show"
      class="qsm-overlay asset-action-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Configuration & Colours"
      @click.self="emit('close')"
    >
      <div class="qsm asset-action-modal">
        <header class="qsm__header modal-header">
          <div class="qsm__header-copy">
            <h3>Configuration</h3>
            <p class="qsm__description">Configure rules for this experience.</p>
          </div>
          <button
            type="button"
            class="qsm__close modal-close-button"
            aria-label="Close settings"
            @click="emit('close')"
          >
            &times;
          </button>
        </header>

        <div class="qsm__scroll-body modal-body">
          <div v-if="isGame" class="qsm__games-placeholder">Games coming soon</div>
          <fieldset v-else class="qsm__group">
            <legend class="qsm__group-title">Rules</legend>
            <div class="qsm__field-grid">
              <label v-if="showTimeLimitField" class="qsm__field">
                <span class="qsm__field-label">Time Limit (seconds)</span>
                <input
                  type="number"
                  :value="draft.timeLimitSeconds"
                  min="10"
                  max="300"
                  @input="draft.timeLimitSeconds = Number(($event.target as HTMLInputElement).value)"
                />
              </label>

              <label v-if="showQuestionsPerRoundField" class="qsm__field">
                <span class="qsm__field-label">Questions per round</span>
                <input
                  type="number"
                  :value="draft.questionsPerRound"
                  :min="QUESTIONS_PER_ROUND_MIN"
                  :max="QUESTIONS_PER_ROUND_MAX"
                  @input="draft.questionsPerRound = clampQuestionsPerRound(Number(($event.target as HTMLInputElement).value))"
                />
              </label>

              <label class="qsm__field">
                <span class="qsm__field-label">Pass Threshold</span>
                <input
                  type="number"
                  :value="draft.passThreshold"
                  :min="1"
                  :max="maxPassThreshold"
                  @input="draft.passThreshold = Number(($event.target as HTMLInputElement).value)"
                />
              </label>

              <label class="qsm__field">
                <span class="qsm__field-label">Max Attempts</span>
                <input
                  type="number"
                  :value="draft.attemptLimit"
                  min="1"
                  max="10"
                  @input="draft.attemptLimit = Number(($event.target as HTMLInputElement).value)"
                />
              </label>

              <label class="qsm__field">
                <span class="qsm__field-label">Attempt Window</span>
                <select
                  :value="draft.attemptWindow"
                  @change="draft.attemptWindow = ($event.target as HTMLSelectElement).value as QuizConfig['attemptWindow']"
                >
                  <option value="week">Per Week</option>
                  <option value="day">Per Day</option>
                  <option value="hour">Per Hour</option>
                  <option value="minute">Per Minute</option>
                  <option value="custom">Custom (per-round)</option>
                </select>
              </label>

              <label v-if="draft.attemptWindow === 'week'" class="qsm__field">
                <span class="qsm__field-label">Week rolls over on</span>
                <select
                  :value="draft.attemptWindowRolloverWeekday"
                  @change="draft.attemptWindowRolloverWeekday = normaliseRolloverWeekday(Number(($event.target as HTMLSelectElement).value))"
                >
                  <option v-for="opt in ROLLOVER_WEEKDAY_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </label>

              <label v-if="draft.attemptWindow === 'week' || draft.attemptWindow === 'day'" class="qsm__field">
                <span class="qsm__field-label">Rollover time (campaign TZ)</span>
                <input
                  type="time"
                  :value="draft.attemptWindowRolloverTime"
                  @change="draft.attemptWindowRolloverTime = normaliseRolloverTime(($event.target as HTMLInputElement).value)"
                />
              </label>

              <div v-if="draft.attemptWindow === 'custom'" class="qsm__field qsm__custom-hint">
                <strong>Custom per-round windows are king.</strong>
                Configure each round's start/end time and max attempts in the
                <em>Experience Editor → Cycling Schedule → Explicit rounds</em>.
                The Attempt Limit + rollover settings above are ignored when this mode is on.
              </div>
            </div>
          </fieldset>

          <fieldset v-if="showLeaderboardSection" class="qsm__group">
            <legend class="qsm__group-title">Leaderboard</legend>
            <div class="qsm__field-grid">
              <label class="qsm__field qsm__field--toggle">
                <span class="qsm__field-label">Enable leaderboard</span>
                <input
                  type="checkbox"
                  :checked="draft.leaderboard.enabled"
                  @change="draft.leaderboard.enabled = ($event.target as HTMLInputElement).checked"
                />
              </label>

              <label class="qsm__field qsm__field--toggle">
                <span class="qsm__field-label">Show after completion only</span>
                <input
                  type="checkbox"
                  :checked="draft.leaderboard.showAfterCompletionOnly"
                  @change="draft.leaderboard.showAfterCompletionOnly = ($event.target as HTMLInputElement).checked"
                />
              </label>

              <label class="qsm__field">
                <span class="qsm__field-label">Leaderboard title</span>
                <input
                  type="text"
                  :value="draft.leaderboard.title"
                  maxlength="48"
                  placeholder="Top Players"
                  @input="draft.leaderboard.title = ($event.target as HTMLInputElement).value"
                />
              </label>

              <label class="qsm__field qsm__field--wide">
                <span class="qsm__field-label">Empty state text</span>
                <input
                  type="text"
                  :value="draft.leaderboard.emptyStateText"
                  maxlength="120"
                  placeholder="Be the first to post a score."
                  @input="draft.leaderboard.emptyStateText = ($event.target as HTMLInputElement).value"
                />
              </label>
            </div>
          </fieldset>

          <!-- Colours hidden — functionality not yet wired up -->
        </div>

        <footer class="qsm__footer modal-footer">
          <div class="qsm__actions">
            <button type="button" class="qsm__action qsm__action--secondary" @click="emit('close')">
              Cancel
            </button>
            <button type="button" class="qsm__action qsm__action--primary" @click="handleSave">
              Save
            </button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { QuizConfig } from '@/types/form.types'

import {
  QUESTIONS_PER_ROUND_DEFAULT,
  QUESTIONS_PER_ROUND_MAX,
  QUESTIONS_PER_ROUND_MIN,
  clampQuestionsPerRound,
} from '@/utils/quizSettings'

interface SettingsPayload {
  timeLimitSeconds: number
  passThreshold: number
  attemptLimit: number
  attemptWindow: QuizConfig['attemptWindow']  // 'week' | 'day' | 'hour' | 'minute'
  attemptWindowRolloverTime: string
  attemptWindowRolloverWeekday: number
  questionsPerRound: number
  leaderboard: NonNullable<QuizConfig['leaderboard']>
  primaryColor: string
  backgroundColor: string
  correctColor: string
  incorrectColor: string
}

type ExperienceTypeProp = 'quiz' | 'survey' | 'game'

const DEFAULT_ROLLOVER_TIME = '00:00'
const DEFAULT_ROLLOVER_WEEKDAY = 0

const ROLLOVER_WEEKDAY_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const normaliseRolloverTime = (value: string | undefined): string => {
  if (!value) return DEFAULT_ROLLOVER_TIME
  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value.trim())
  if (!match) return DEFAULT_ROLLOVER_TIME
  const hours = Math.min(23, Math.max(0, Number(match[1]) || 0))
  const minutes = Math.min(59, Math.max(0, Number(match[2]) || 0))
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const normaliseRolloverWeekday = (value: number | undefined): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_ROLLOVER_WEEKDAY
  return ((Math.trunc(value) % 7) + 7) % 7
}

const props = withDefaults(defineProps<{
  show: boolean
  inline?: boolean
  config: QuizConfig
  maxPassThreshold: number
  experienceType?: ExperienceTypeProp
}>(), {
  experienceType: 'quiz',
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', value: SettingsPayload): void
  (e: 'attempt-window-change', payload: { previous: QuizConfig['attemptWindow']; next: QuizConfig['attemptWindow'] }): void
}>()

const isSurvey = computed(() => props.experienceType === 'survey')
const isGame = computed(() => props.experienceType === 'game')
const showTimeLimitField = computed(() => !isSurvey.value && !isGame.value)
const showLeaderboardSection = computed(() => !isSurvey.value && !isGame.value)
const showQuestionsPerRoundField = computed(() => !isSurvey.value && !isGame.value)

const draft = reactive<SettingsPayload>({
  timeLimitSeconds: props.config.timeLimitSeconds,
  passThreshold: props.config.passThreshold,
  attemptLimit: props.config.attemptLimit,
  attemptWindow: props.config.attemptWindow,
  attemptWindowRolloverTime: normaliseRolloverTime(props.config.attemptWindowRolloverTime),
  attemptWindowRolloverWeekday: normaliseRolloverWeekday(props.config.attemptWindowRolloverWeekday),
  questionsPerRound: clampQuestionsPerRound(props.config.questionsPerRound),
  leaderboard: {
    enabled: props.config.leaderboard?.enabled === true,
    eventName: props.config.leaderboard?.eventName || 'quiz.completed.score',
    displayMode: 'top-10-plus-user',
    title: props.config.leaderboard?.title || 'Top Players',
    emptyStateText: props.config.leaderboard?.emptyStateText || 'Be the first to post a score.',
    showAfterCompletionOnly: props.config.leaderboard?.showAfterCompletionOnly !== false,
  },
  primaryColor: props.config.primaryColor,
  backgroundColor: props.config.backgroundColor,
  correctColor: props.config.correctColor,
  incorrectColor: props.config.incorrectColor,
})
const saveSuccess = ref('')

watch(() => [props.show, props.inline, props.config] as const, ([visible, inline]) => {
  if (!visible && !inline) return
  draft.timeLimitSeconds = props.config.timeLimitSeconds
  draft.passThreshold = props.config.passThreshold
  draft.attemptLimit = props.config.attemptLimit
  draft.attemptWindow = props.config.attemptWindow
  draft.attemptWindowRolloverTime = normaliseRolloverTime(props.config.attemptWindowRolloverTime)
  draft.attemptWindowRolloverWeekday = normaliseRolloverWeekday(props.config.attemptWindowRolloverWeekday)
  draft.questionsPerRound = clampQuestionsPerRound(props.config.questionsPerRound)
  draft.leaderboard.enabled = props.config.leaderboard?.enabled === true
  draft.leaderboard.eventName = props.config.leaderboard?.eventName || 'quiz.completed.score'
  draft.leaderboard.displayMode = 'top-10-plus-user'
  draft.leaderboard.title = props.config.leaderboard?.title || 'Top Players'
  draft.leaderboard.emptyStateText = props.config.leaderboard?.emptyStateText || 'Be the first to post a score.'
  draft.leaderboard.showAfterCompletionOnly = props.config.leaderboard?.showAfterCompletionOnly !== false
  draft.primaryColor = props.config.primaryColor
  draft.backgroundColor = props.config.backgroundColor
  draft.correctColor = props.config.correctColor
  draft.incorrectColor = props.config.incorrectColor
  if (!inline) {
    saveSuccess.value = ''
  }
}, { immediate: true, deep: true })

// Notify parent when the attempt window changes so it can rename theme-default
// round labels (week → day → hour → minute). Manually edited labels are
// preserved by the parent's rename helper. We watch the draft (not the
// committed config) so the cascade fires immediately, before Save.
watch(
  () => draft.attemptWindow,
  (next, previous) => {
    if (!previous || previous === next) return
    emit('attempt-window-change', { previous, next })
  },
)

function handleSave() {
  saveSuccess.value = props.inline ? 'Settings saved into this draft.' : ''
  emit('save', {
    ...draft,
    questionsPerRound: clampQuestionsPerRound(draft.questionsPerRound),
  })
}
</script>

<style scoped>
.qsm__games-placeholder {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.72));
  font-size: 0.92rem;
  font-style: italic;
  border: 1px dashed var(--ui-inverse-border, rgba(122, 230, 177, 0.2));
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.04);
}

.qsm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: calc(var(--base-layout-modal-z-index) + 200);
}

.qsm {
  --qsm-scale: 1;
  width: min(560px, 100%);
  max-height: min(92vh, 720px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qsm--inline {
  width: 100%;
  min-width: 100%;
  max-height: none;
  min-height: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.qsm--inline .qsm__header-copy,
.qsm--inline .qsm__scroll-body,
.qsm--inline .qsm__group,
.qsm--inline .qsm__field-grid {
  max-width: none;
  width: 100%;
}

.qsm--inline .qsm__header h3,
.qsm--inline .qsm__description,
.qsm--inline .qsm__group-title,
.qsm--inline .qsm__field,
.qsm--inline .qsm__field-label,
.qsm--inline .qsm__color-hex {
  color: #f5fbff;
}

.qsm--inline .qsm__description,
.qsm--inline .qsm__field-label,
.qsm--inline .qsm__color-hex {
  color: rgba(237, 244, 255, 0.72);
}

.qsm--inline .qsm__group {
  border-color: rgba(122, 230, 177, 0.1);
  background: rgba(255, 255, 255, 0.04);
}

.qsm--inline .qsm__field {
  border-color: rgba(122, 230, 177, 0.12);
  background: rgba(5, 13, 24, 0.4);
}

.qsm--inline .qsm__field input:not([type='color']),
.qsm--inline .qsm__field select {
  color: #f5fbff;
  background: rgba(5, 13, 24, 0.52);
  border-color: rgba(122, 230, 177, 0.12);
}

.qsm--inline .qsm__field input:not([type='color'])::placeholder {
  color: rgba(237, 244, 255, 0.45);
}

.qsm--inline .qsm__field--toggle input[type='checkbox'] {
  accent-color: rgba(122, 230, 177, 0.96);
}

.qsm__custom-hint {
  margin-top: 0.5rem;
  padding: 0.65rem 0.85rem;
  border-radius: 0.5rem;
  background: rgba(122, 230, 177, 0.08);
  border: 0.0625rem solid rgba(122, 230, 177, 0.25);
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.82rem;
  line-height: 1.4;
}

.qsm__custom-hint strong {
  display: block;
  margin-bottom: 0.25rem;
  color: rgba(122, 230, 177, 0.96);
  font-weight: 600;
}

.qsm__custom-hint em {
  font-style: normal;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
}

.qsm--inline .qsm__action--primary {
  border-color: rgba(122, 230, 177, 0.96);
  background: rgba(122, 230, 177, 0.96);
  color: #06111b;
}

.qsm--inline .qsm__action--primary:hover {
  border-color: rgba(122, 230, 177, 0.82);
  background: rgba(122, 230, 177, 0.82);
}

.qsm__header--inline {
  padding: 1rem 1rem 0.6rem;
}

.qsm__action--inline-save {
  width: 100%;
  min-width: 0;
  min-height: 1.7rem;
  padding: 0.24rem 0.6rem;
  font-size: 0.76rem;
}

.qsm__header {
  flex-shrink: 0;
}

.qsm__header-copy {
  display: grid;
  gap: 0.35rem;
  max-width: min(100%, 24rem);
  text-align: left;
  justify-items: start;
  align-self: flex-start;
}

.qsm__header-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  margin-left: auto;
  width: 9.5rem;
  min-height: 5.55rem;
  padding: 0.5rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.12);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  box-sizing: border-box;
}

.qsm__step-actions-label {
  width: 100%;
  color: #f5fbff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.qsm__step-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 1.65rem;
  padding: 0.22rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(237, 244, 255, 0.86);
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.qsm__step-status--saved {
  border-color: rgba(122, 230, 177, 0.3);
  background: rgba(122, 230, 177, 0.12);
  color: rgba(122, 230, 177, 0.96);
}

.qsm__header h3 {
  margin-bottom: 0;
}

.qsm__description {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.92rem;
  line-height: 1.45;
}

@media (max-width: 900px) {
  .qsm__header--inline {
    align-items: flex-start;
  }

  .qsm__header-actions {
    margin-left: 0;
    width: 100%;
  }
}

.qsm__scroll-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.65rem 1.25rem 1rem;
}

.qsm__scroll-body--inline {
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 0.25rem 1rem 1.5rem;
}

.qsm__group {
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.85rem;
  padding: 0.85rem;
  margin: 0 0 0.65rem;
  background: rgba(230, 255, 244, 0.2);
}

.qsm__group-title {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ui-inverse-text);
  padding: 0 0.3rem;
}

.qsm__field-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.65rem;
  margin-top: 0.55rem;
}

.qsm__field {
  display: grid;
  gap: 0.4rem;
  color: var(--ui-inverse-text);
  padding: 0.7rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 26, 52, 0.12);
  background: rgba(16, 26, 52, 0.06);
}

.qsm__field--wide {
  grid-column: 1 / -1;
}

.qsm__field--toggle {
  display: flex !important;
  flex-direction: row !important;
  justify-content: space-between;
  align-items: center !important;
  min-height: 0;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.4rem 0.6rem !important;
}

.qsm__field--toggle .qsm__field-label {
  flex: 1 1 auto;
  min-width: 0;
  display: block;
  font-size: 0.72rem;
}

.qsm__field--toggle input[type='checkbox'] {
  width: 1.15rem !important;
  height: 1.15rem !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 4px !important;
  border: 1px solid var(--ui-inverse-border, rgba(122, 230, 177, 0.2)) !important;
  background: transparent !important;
  box-shadow: none !important;
  cursor: pointer;
  accent-color: var(--ui-inverse-cta-bg, #37c88d);
  flex-shrink: 0;
}

.qsm__toggle {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  width: 3.05rem;
  height: 1.85rem;
  margin: 0 0 0 auto;
  padding: 0.14rem;
  border: 1px solid rgba(122, 230, 177, 0.22);
  border-radius: 999px;
  background: rgba(5, 13, 24, 0.5);
  transition: background-color 140ms ease, border-color 140ms ease;
}

.qsm__toggle--active {
  background: rgba(122, 230, 177, 0.22);
  border-color: rgba(122, 230, 177, 0.75);
}

.qsm__toggle-thumb {
  display: block;
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 999px;
  background: #f5fbff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
  transform: translateX(0);
  transition: transform 140ms ease;
}

.qsm__toggle--active .qsm__toggle-thumb {
  transform: translateX(1.14rem);
}

.qsm__field-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ui-inverse-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qsm__field input:not([type='color']),
.qsm__field select {
  width: 100%;
  min-height: 2.85rem;
  border-radius: 999px;
  padding: 0.7rem 0.9rem;
}

.qsm__color-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.qsm__color-swatch {
  width: 36px !important;
  min-width: 36px;
  height: 36px !important;
  min-height: 36px !important;
  padding: 2px !important;
  border-radius: 0.6rem !important;
  border: 1px solid rgba(16, 26, 52, 0.22) !important;
  cursor: pointer;
  flex-shrink: 0;
  background: none;
  box-shadow: none;
}

.qsm__color-hex {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.82rem;
  color: var(--ui-inverse-text-muted);
}

.qsm__footer {
  flex-shrink: 0;
}

.qsm__footer--inline {
  padding: 0 1rem 1rem;
}

.qsm__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  width: 100%;
}

.qsm__actions--inline {
  justify-content: flex-start;
}

.qsm__actions--inline > button {
  flex: 0 0 auto;
  min-width: 10rem;
}

.qsm__actions > button {
  flex: 1 1 0;
}

.qsm__action {
  min-height: 2.85rem;
  margin: 0;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-family: var(--ui-inverse-font-family);
  font-weight: 700;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}

.qsm__action--secondary {
  border: 1px solid var(--ui-inverse-border-strong);
  background: transparent;
  color: var(--ui-inverse-text);
}

.qsm__action--secondary:hover {
  background: rgba(16, 26, 52, 0.08);
  border-color: rgba(16, 26, 52, 0.36);
}

.qsm__action--primary {
  border: 1px solid var(--ui-inverse-cta-bg);
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
}

.qsm__action--primary:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.qsm__header-actions .qsm__action--inline-save {
  min-height: 1.7rem !important;
  padding: 0.24rem 0.6rem !important;
  font-size: 0.76rem !important;
  text-align: center !important;
}

@media (min-width: 769px) {
  .qsm {
    --qsm-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
    width: min(calc(560px * var(--qsm-scale)), 100%);
    max-height: min(92vh, calc(720px * var(--qsm-scale)));
  }

  .qsm__header {
    padding:
      max(1.1rem, calc(1.5rem * var(--qsm-scale)))
      max(2.2rem, calc(3.5rem * var(--qsm-scale)));
  }

  .qsm__description {
    font-size: calc(0.92rem * var(--qsm-scale));
  }

  .qsm__scroll-body {
    padding:
      max(0.5rem, calc(0.65rem * var(--qsm-scale)))
      max(1rem, calc(1.25rem * var(--qsm-scale)))
      max(0.8rem, calc(1rem * var(--qsm-scale)));
  }

  .qsm__group {
    padding: max(0.72rem, calc(0.85rem * var(--qsm-scale)));
    margin-bottom: max(0.55rem, calc(0.65rem * var(--qsm-scale)));
    border-radius: max(0.72rem, calc(0.85rem * var(--qsm-scale)));
  }

  .qsm__group-title {
    font-size: calc(0.78rem * var(--qsm-scale));
  }

  .qsm__field-grid {
    gap: max(0.55rem, calc(0.65rem * var(--qsm-scale)));
    margin-top: max(0.45rem, calc(0.55rem * var(--qsm-scale)));
  }

  .qsm__field {
    gap: max(0.32rem, calc(0.4rem * var(--qsm-scale)));
    padding: max(0.58rem, calc(0.7rem * var(--qsm-scale)));
    border-radius: max(0.65rem, calc(0.75rem * var(--qsm-scale)));
  }

  .qsm__field-label {
    font-size: calc(0.75rem * var(--qsm-scale));
  }

  .qsm__field input:not([type='color']),
  .qsm__field select {
    min-height: calc(2.85rem * var(--qsm-scale));
    padding: calc(0.7rem * var(--qsm-scale)) calc(0.9rem * var(--qsm-scale));
  }

  .qsm__color-swatch {
    width: calc(36px * var(--qsm-scale)) !important;
    min-width: calc(36px * var(--qsm-scale));
    height: calc(36px * var(--qsm-scale)) !important;
    min-height: calc(36px * var(--qsm-scale)) !important;
    border-radius: max(0.5rem, calc(0.6rem * var(--qsm-scale))) !important;
  }

  .qsm__color-hex {
    font-size: calc(0.82rem * var(--qsm-scale));
  }

  .qsm__action {
    min-height: calc(2.85rem * var(--qsm-scale));
    padding: calc(0.65rem * var(--qsm-scale)) calc(1rem * var(--qsm-scale));
    font-size: calc(0.94rem * var(--qsm-scale));
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  .qsm-overlay {
    align-items: flex-start;
    padding: 1.25rem 1rem;
  }
}

@media (max-width: 640px) {
  .qsm {
    max-height: min(94vh, 720px);
  }

  .qsm__header,
  .qsm__scroll-body,
  .qsm__actions {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .qsm__field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
